/**
 * Customer React Query defaults — kept outside AppDataProvider so Vite Fast
 * Refresh can update the provider without invalidating non-component exports.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/ApiClient.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';

/**
 * Wave 0 / S6 — skip React Query retries that would double-feed the auth
 * interceptor (401) or CSRF/forbidden (403). Also skip network / 429 / 5xx
 * so a dying proxy does not stampede GETs.
 * Wave D — while HTTP storm latch is armed, never retry 429 (or overload).
 */
export function shouldRetryCustomerQuery(
  failureCount: number,
  error: Error,
): boolean {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return false;
    }
    if (
      error.status === 0 ||
      error.code === 'NETWORK_ERROR' ||
      error.status === 429 ||
      error.status >= 500
    ) {
      return false;
    }
  }
  // Raw fetch/proxy failures (TurnstileConfigFetchError, TypeError, AggregateError)
  // are not ApiError — still BAN retries so a dead :3015 cannot stampede Vite.
  const name = error.name;
  if (
    name === 'TurnstileConfigFetchError' ||
    name === 'TypeError' ||
    name === 'AggregateError' ||
    name === 'AbortError'
  ) {
    return false;
  }
  const message = error.message.toLowerCase();
  if (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('unreachable') ||
    message.includes('econnrefused')
  ) {
    return false;
  }
  // Belt: latch-armed 429 must never retry even if error shape drifts.
  if (isHttpStormLatched() && error instanceof ApiError && error.status === 429) {
    return false;
  }
  return failureCount < 1;
}

/**
 * G8 — BAN reconnect refetch stampede while HTTP-storm latched.
 */
export function shouldRefetchCustomerOnReconnect(): boolean {
  return !isHttpStormLatched();
}

export function createCustomerQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetryCustomerQuery,
        refetchOnWindowFocus: false,
        // Wave D / G8 — BAN reconnect stampede while storm-latched.
        refetchOnReconnect: shouldRefetchCustomerOnReconnect,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
